import { AlertTriangleIcon, Loader2Icon, XIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'

export type ConfirmDestroyModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  targetName: string
  confirmText?: string
  cancelText?: string
  isLoading?: boolean
}

const ConfirmDestroyModal: React.FC<ConfirmDestroyModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  targetName,
  confirmText = 'Destruir',
  cancelText = 'Cancelar',
  isLoading = false,
}) => {
  const [confirmName, setConfirmName] = useState('')

  useEffect(() => {
    if (!isOpen) setConfirmName('')
  }, [isOpen])

  if (!isOpen) return null

  const canConfirm = confirmName === targetName && !isLoading

  const handleConfirm = () => {
    if (!canConfirm) return
    onConfirm()
  }

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
      <div className="absolute inset-0" onClick={isLoading ? undefined : onClose} />

      <div className="relative mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center space-x-3">
            <AlertTriangleIcon className="h-6 w-6 text-red-500" />
            <h3 className="text-lg font-semibold text-red-800">{title}</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 pb-4">
          <p className="text-sm whitespace-pre-line text-gray-600">{message}</p>

          <div>
            <label className="mb-1 block text-xs text-slate-500">
              Digite{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-red-600">
                {targetName}
              </code>{' '}
              para confirmar
            </label>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={targetName}
              autoFocus
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canConfirm) handleConfirm()
              }}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-red-300 focus:ring-2 focus:ring-red-500/10 disabled:bg-slate-50 disabled:opacity-50"
            />
          </div>
        </div>

        <div className="flex space-x-3 border-t border-gray-200 p-6 pt-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="focus:ring-opacity-50 inline-flex flex-1 items-center justify-center gap-2 rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isLoading ? (
              <>
                <Loader2Icon className="h-4 w-4 animate-spin" />
                Destruindo...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConfirmDestroyModal
